import { useQueries, useQuery } from '@tanstack/react-query';
import type { Screen } from '@ghostscreen/contracts';
import { api } from '../../lib/api';
import { getCardConnectionStatus } from '../../lib/dashboard/screenStatus';

/**
 * Loads default playlist assignments for paired screens.
 * Used by the playlist drawer “Put it on screens” chips.
 */
export function useScreenPlaylistAssignments(organisationId: string) {
  const screensQuery = useQuery({
    queryKey: ['screens', organisationId],
    queryFn: () => api.listScreens(organisationId),
    refetchInterval: 15_000,
  });

  const screens = (screensQuery.data ?? []).filter(
    (screen) => getCardConnectionStatus(screen) !== 'unpaired',
  );

  const assignmentQueries = useQueries({
    queries: screens.map((screen) => ({
      queryKey: ['screen-assignment', organisationId, screen.id],
      queryFn: () => api.getScreenAssignment(organisationId, screen.id),
    })),
  });

  const assignmentByScreenId = new Map<string, string | null>();
  screens.forEach((screen, index) => {
    const query = assignmentQueries[index];
    if (!query || query.isPending || query.isError) {
      assignmentByScreenId.set(screen.id, null);
      return;
    }
    assignmentByScreenId.set(screen.id, query.data?.playlistId ?? null);
  });

  const screenCountByPlaylistId = new Map<string, number>();
  for (const playlistId of assignmentByScreenId.values()) {
    if (!playlistId) continue;
    screenCountByPlaylistId.set(playlistId, (screenCountByPlaylistId.get(playlistId) ?? 0) + 1);
  }

  const isPending =
    screensQuery.isPending ||
    (screens.length > 0 && assignmentQueries.some((query) => query.isPending));

  return {
    screens: screens as Screen[],
    assignmentByScreenId,
    screenCountByPlaylistId,
    isPending,
    isError: screensQuery.isError,
  };
}
